import {ModalState} from "../../../types/modals";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {Field, FormLanguage, PlainDetails} from "../../../types/admin";
import {AVAILABLE_LANGUAGES} from "../../../settings";
import {deleteResult, updateResults} from "../adminList";
import {changeGameModal} from "./gameEditModal";
import {toastSuccess} from "../admin";

export type FormModalState = ModalState & {
    form_id: string | null
    languages: FormLanguage[]
    form_fields: Field[]
    languageTab: string
    used_in: boolean
    game_id?: string
    game_form?: string
    delete_message: string
}

const initialState: FormModalState = {
    loading: false,
    isOpen: false,
    error: null,
    form_id: null,
    languages: [],
    form_fields: [],
    languageTab: AVAILABLE_LANGUAGES[0],
    used_in: false,
    delete_message: '',
}

export type NewFormPayload = {
    languages: FormLanguage[]
    form_fields: Field[]
    game_id?: string
    game_form?: string
}

export type EditFormPayload = NewFormPayload & {
    form_id: string
}

export const getForm = createAsyncThunk(
    'formModal/getForm',
    async (form_id: string, thunkAPI) => {
        try {
            return await api.get(`/API/admin/forms/${form_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveForm = createAsyncThunk(
    'formModal/saveForm',
    async (payload: EditFormPayload, thunkAPI) => {
        const {form_id} = payload;
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.patch(`/API/admin/forms/${form_id}/`, payload);
            dispatch(updateResults({result: response}));
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewForm = createAsyncThunk(
    'formModal/saveNewForm',
    async (payload: NewFormPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const {game_id, game_form} = payload;
            const response = await api.post(`/API/admin/forms/`, payload);
            if (game_id && game_form) {
                const { form_id, name } = response;
                const formDetails: PlainDetails = {
                    value: form_id,
                    label: name
                }
                if (game_form === 'start') {
                    dispatch(changeGameModal({form: formDetails}));
                } else if (game_form === 'end') {
                    dispatch(changeGameModal({end_form: formDetails}));
                }
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteForm = createAsyncThunk(
    'formModal/deleteForm',
    async (form_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/forms/${form_id}/`);
            if(response.key){
                dispatch(deleteResult({...response}));
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


const slice = createSlice({
    name: 'formModal',
    initialState,
    reducers: {
        openModal: (state, action: PayloadAction<{ form_id: string | null , game_id?: string, game_form?: string}>) => {
            state.isOpen = true;
            state.form_id = action.payload.form_id;
            if (action.payload.game_id && action.payload.game_form) {
                state.game_id = action.payload.game_id;
                state.game_form = action.payload.game_form;
            }
        },
        closeModal: () => {
            return initialState;
        },
        changeModal: (state, action: PayloadAction<Partial<FormModalState>>) => {
            return {...state, ...action.payload};
        },
        setActiveLanguage: (state, action: PayloadAction<string>) => {
            state.languageTab = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(deleteForm.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteForm.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteForm.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getForm.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getForm.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.form_fields = action.payload.form_fields;
                state.languages = action.payload.languages;
                state.used_in = action.payload.used_in;
                state.delete_message = action.payload.delete_message;
            })
            .addCase(getForm.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveForm.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveForm.fulfilled, () => {
                return initialState;
            })
            .addCase(saveForm.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewForm.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewForm.fulfilled, () => {
                return initialState;
            })
            .addCase(saveNewForm.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    openModal,
    closeModal,
    setActiveLanguage,
    changeModal
} = slice.actions;
export default slice.reducer;
